import { useState } from "react";
import { EXTRA_CONTRIBUTION_DEFAULT } from "web_myplann/Goals/constants";
import { calculateExtraContributionImpact } from "web_myplann/Goals/Helpers/extraContributionImpact";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { formatMonthsSaved } from "web_myplann/Goals/Helpers/formatMonthsSaved";
import { getGoalMonthlySavings } from "web_myplann/Goals/Helpers/goalMonthlySavings";
import {
	type ExtraContributionScenarioInfo,
	useExtraContributionScenarios,
} from "web_myplann/Goals/Hooks/useExtraContributionScenarios";
import { useGoalAdvisorChat } from "web_myplann/Goals/Hooks/useGoalAdvisorChat";
import type { ExtraContributionImpact } from "web_myplann/Goals/Types/ExtraContributionImpact";
import { ExtraContributionScenario } from "web_myplann/Goals/Types/ExtraContributionScenario";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type ExtraContributionPlan = {
	isVisible: boolean;
	extraAmount: number;
	setExtraAmount: (value: number) => void;
	activeScenario: ExtraContributionScenario;
	setActiveScenario: (scenario: ExtraContributionScenario) => void;
	scenarios: ExtraContributionScenario[];
	scenarioInfo: Record<
		ExtraContributionScenario,
		ExtraContributionScenarioInfo
	>;
	impact: ExtraContributionImpact;
	savedLabel: string;
	openAdvisorChat: () => void;
};

export const useExtraContributionPlan = (
	goal: GoalDetail,
): ExtraContributionPlan => {
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const openChat = useGoalAdvisorChat(goal);

	const [extraAmount, setExtraAmount] = useState(EXTRA_CONTRIBUTION_DEFAULT);
	const monthlySavings = getGoalMonthlySavings(goal);
	const canFinishEarlier = monthlySavings > 0;
	const hasPinnedOrPlannedContract =
		goal.portfolios.length > 0 || goal.futurePortfolios.length > 0;
	const [activeScenario, setActiveScenario] =
		useState<ExtraContributionScenario>(
			canFinishEarlier
				? ExtraContributionScenario.Earlier
				: ExtraContributionScenario.More,
		);

	const impact = calculateExtraContributionImpact(
		{
			dueYear: goal.dueYear,
			savingsTarget: goal.savingsTarget,
			monthlySavings,
		},
		extraAmount,
	);
	const savedLabel = formatMonthsSaved(impact.monthsSaved, t);

	const { scenarios, scenarioInfo } = useExtraContributionScenarios({
		canFinishEarlier,
		savedLabel,
		extraTotal: impact.extraTotal,
		currencyCode: goal.currencyCode,
	});

	const openAdvisorChat = () =>
		openChat(
			t(Resources.Goals.Detail.ExtraContribution.advisorMessage, {
				goal: goal.name,
				extra: formatAmount(extraAmount, goal.currencyCode),
			}),
		);

	return {
		isVisible:
			!goal.completed &&
			goal.savingsTarget > 0 &&
			impact.yearsLeft > 0 &&
			hasPinnedOrPlannedContract,
		extraAmount,
		setExtraAmount,
		activeScenario: scenarios.includes(activeScenario)
			? activeScenario
			: scenarios[0],
		setActiveScenario,
		scenarios,
		scenarioInfo,
		impact,
		savedLabel,
		openAdvisorChat,
	};
};
